import { useRef, useState } from "react";
import axios from "axios";
import { Camera, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const OCR_URL = `${BACKEND_URL}/api/ocr`;

/* DateWise Wingman — screenshot uploader.
 *
 * Props:
 *   label       Button text (e.g. "Upload Screenshot" or "Upload profile screenshot(s)")
 *   onExtracted (text) => void  — called with the OCR'd plain text
 *   testId      data-testid prefix for the input + button
 */
export const ScreenshotUploader = ({
  label = "Upload Screenshot",
  onExtracted,
  testId = "ocr",
}) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [count, setCount] = useState(0);

  const trigger = () => {
    if (loading) return;
    setError("");
    inputRef.current && inputRef.current.click();
  };

  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError("");
    setLoading(true);
    setCount(files.length);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const res = await axios.post(OCR_URL, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      if (res.data && res.data.ok) {
        onExtracted(res.data.text || "");
      } else {
        setError("Couldn't read that screenshot. Try a clearer image.");
      }
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.detail) ||
        "Couldn't read that screenshot. Try a clearer image.";
      setError(typeof msg === "string" ? msg : "Couldn't read that screenshot.");
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mt-3" data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        onChange={onChange}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={trigger}
          disabled={loading}
          className="ss-ghost"
          data-testid={`${testId}-btn`}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.25} />
          ) : (
            <Camera className="w-4 h-4" strokeWidth={2.25} />
          )}
          {loading
            ? `Reading ${count} screenshot${count === 1 ? "" : "s"}\u2026`
            : label}
        </button>
        <span
          className="text-xs"
          style={{ color: "var(--ss-muted)" }}
          data-testid={`${testId}-privacy`}
        >
          Private and temporary. Your text and screenshots are processed privately and not stored long-term. We do not store your chat histories or sell your identity.
        </span>
      </div>
      {error && (
        <p
          className="mt-2 text-sm"
          style={{ color: "var(--ss-red)" }}
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
};
